import { useState, useEffect } from "react";
import { ArrowLeft, Users, Search, Calendar, Phone, Mail, CreditCard, BookOpen, ChevronUp, ChevronDown, RefreshCw } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TIER_LABELS = {
  technical_analysis: "Technical Analysis",
  investment_base: "Investment Base",
  full_mentor: "Full Mentor",
};

const TIER_COLORS = {
  technical_analysis: { bg: "rgba(59,130,246,0.15)", color: "#60a5fa" },
  investment_base: { bg: "rgba(168,85,247,0.15)", color: "#c084fc" },
  full_mentor: { bg: "rgba(34,197,94,0.15)", color: "#22c55e" },
};

function formatDate(isoStr) {
  if (!isoStr) return "—";
  const d = new Date(isoStr);
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

export default function AdminDashboard({ session, onBack }) {
  const [students, setStudents] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [sortField, setSortField] = useState("registered_at");
  const [sortDir, setSortDir] = useState("desc");
  const [filterTier, setFilterTier] = useState("all");

  const headers = { Authorization: `Bearer ${session.access_token}` };

  const fetchStudents = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/students`, { headers });
      if (res.ok) {
        const data = await res.json();
        setStudents(data.students);
        setTotal(data.total);
      }
    } catch (e) { /* silent */ }
    setLoading(false);
  };

  useEffect(() => { fetchStudents(); }, []);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDir("asc");
    }
  };

  const renderSortIcon = (field) => {
    if (sortField !== field) return null;
    return sortDir === "asc" ? <ChevronUp style={{ width: 12, height: 12, display: "inline", verticalAlign: "middle", marginLeft: 4 }} /> : <ChevronDown style={{ width: 12, height: 12, display: "inline", verticalAlign: "middle", marginLeft: 4 }} />;
  };

  const filtered = students
    .filter((s) => {
      if (filterTier !== "all" && s.plan_tier !== filterTier) return false;
      if (!search) return true;
      const q = search.toLowerCase();
      return (
        (s.email && s.email.toLowerCase().includes(q)) ||
        (s.full_name && s.full_name.toLowerCase().includes(q)) ||
        (s.phone && s.phone.includes(q))
      );
    })
    .sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];

      // Special handling for next_booking: sort by date+time, nulls always last
      if (sortField === "next_booking") {
        const aKey = a.next_booking ? `${a.next_booking.date}_${a.next_booking.time}` : null;
        const bKey = b.next_booking ? `${b.next_booking.date}_${b.next_booking.time}` : null;
        if (!aKey && !bKey) return 0;
        if (!aKey) return 1;
        if (!bKey) return -1;
        return sortDir === "asc" ? aKey.localeCompare(bKey) : bKey.localeCompare(aKey);
      }

      if (aVal == null) aVal = "";
      if (bVal == null) bVal = "";
      if (sortField === "lessons_remaining") {
        return sortDir === "asc" ? aVal - bVal : bVal - aVal;
      }
      return sortDir === "asc"
        ? String(aVal).localeCompare(String(bVal))
        : String(bVal).localeCompare(String(aVal));
    });

  const paidCount = students.filter((s) => s.plan_status === "paid").length;
  const freeCount = students.filter((s) => s.plan_status !== "paid").length;
  const upcomingCount = students.filter((s) => s.next_booking).length;

  // Inline styles to guarantee layout (bypasses Tailwind resets)
  const S = {
    page: { display: "block", width: "100%", maxWidth: 1400, margin: "0 auto", padding: "100px 32px 48px", boxSizing: "border-box" },
    topBar: { display: "flex", alignItems: "center", gap: 20, marginBottom: 28 },
    backBtn: { display: "inline-flex", alignItems: "center", gap: 6, color: "rgba(255,255,255,0.5)", background: "none", border: "none", cursor: "pointer", fontSize: "0.85rem" },
    title: { fontFamily: "'Manrope', sans-serif", fontSize: "1.5rem", fontWeight: 700, color: "#F5F5F7", margin: 0 },
    statsGrid: { display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16, marginBottom: 28, width: "100%" },
    statCard: { display: "flex", flexDirection: "row", alignItems: "center", gap: 14, padding: "20px 22px", background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 12 },
    statIcon: { color: "#22c55e", flexShrink: 0, width: 22, height: 22 },
    statValue: { fontSize: "1.5rem", fontWeight: 700, lineHeight: 1.2, color: "#F5F5F7", margin: 0 },
    statLabel: { fontSize: "0.75rem", color: "rgba(255,255,255,0.45)", margin: "2px 0 0" },
    controls: { display: "flex", flexDirection: "row", gap: 12, alignItems: "center", marginBottom: 20, width: "100%" },
    searchWrap: { position: "relative", flex: "1 1 auto", minWidth: 240 },
    searchIcon: { position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "rgba(255,255,255,0.3)", pointerEvents: "none", width: 16, height: 16 },
    searchInput: { display: "block", width: "100%", padding: "10px 14px 10px 38px", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, color: "#F5F5F7", fontSize: "0.85rem", outline: "none", boxSizing: "border-box" },
    filterSelect: { padding: "10px 14px", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, color: "#F5F5F7", fontSize: "0.85rem", cursor: "pointer", outline: "none", flexShrink: 0 },
    refreshBtn: { display: "inline-flex", alignItems: "center", gap: 6, padding: "10px 16px", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, color: "rgba(255,255,255,0.6)", fontSize: "0.85rem", cursor: "pointer", whiteSpace: "nowrap", flexShrink: 0 },
    tableWrap: { overflowX: "auto", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 12, background: "rgba(255,255,255,0.02)", width: "100%" },
    table: { width: "100%", minWidth: 960, borderCollapse: "collapse", fontSize: "0.84rem", tableLayout: "fixed" },
    th: { padding: "14px 16px", textAlign: "left", fontWeight: 600, color: "rgba(255,255,255,0.5)", fontSize: "0.72rem", textTransform: "uppercase", letterSpacing: "0.06em", borderBottom: "1px solid rgba(255,255,255,0.08)", whiteSpace: "nowrap", userSelect: "none", cursor: "pointer" },
    td: { padding: "14px 16px", borderBottom: "1px solid rgba(255,255,255,0.05)", verticalAlign: "top", overflow: "hidden" },
    footer: { padding: "16px 0", textAlign: "center", color: "rgba(255,255,255,0.3)", fontSize: "0.8rem" },
  };

  // Column widths for fixed table layout
  const colWidths = ["12%", "24%", "14%", "10%", "8%", "16%", "16%"];

  return (
    <div style={S.page} data-testid="admin-dashboard">
      <div style={S.topBar}>
        <button style={S.backBtn} onClick={onBack} data-testid="admin-back-btn">
          <ArrowLeft style={{ width: 16, height: 16 }} /> Back to Dashboard
        </button>
        <h1 style={S.title} data-testid="admin-title">Admin Panel</h1>
      </div>

      {/* Stats Row */}
      <div style={S.statsGrid} data-testid="admin-stats">
        {[
          { icon: Users, value: total, label: "Total Students", testId: "stat-total" },
          { icon: CreditCard, value: paidCount, label: "Paid", testId: "stat-paid" },
          { icon: BookOpen, value: freeCount, label: "Free", testId: "stat-free" },
          { icon: Calendar, value: upcomingCount, label: "Upcoming Lessons", testId: "stat-upcoming" },
        ].map(({ icon: Icon, value, label, testId }) => (
          <div key={testId} style={S.statCard}>
            <Icon style={S.statIcon} />
            <div>
              <p style={S.statValue} data-testid={testId}>{value}</p>
              <p style={S.statLabel}>{label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Controls */}
      <div style={S.controls} data-testid="admin-controls">
        <div style={S.searchWrap}>
          <Search style={S.searchIcon} />
          <input
            type="text"
            placeholder="Search by name, email, or phone..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={S.searchInput}
            data-testid="admin-search"
          />
        </div>
        <select
          value={filterTier}
          onChange={(e) => setFilterTier(e.target.value)}
          style={S.filterSelect}
          data-testid="admin-filter-tier"
        >
          <option value="all">All Plans</option>
          <option value="technical_analysis">Technical Analysis</option>
          <option value="investment_base">Investment Base</option>
          <option value="full_mentor">Full Mentor</option>
        </select>
        <button style={S.refreshBtn} onClick={fetchStudents} data-testid="admin-refresh-btn">
          <RefreshCw style={{ width: 16, height: 16, ...(loading ? { animation: "spin 1s linear infinite" } : {}) }} /> Refresh
        </button>
      </div>

      {/* Table */}
      <div style={S.tableWrap} data-testid="admin-table-wrap">
        {loading ? (
          <div style={{ padding: 60, textAlign: "center", color: "rgba(255,255,255,0.4)" }} data-testid="admin-loading">Loading students...</div>
        ) : (
          <table style={S.table} data-testid="admin-table">
            <colgroup>
              {colWidths.map((w, i) => <col key={i} style={{ width: w }} />)}
            </colgroup>
            <thead>
              <tr style={{ background: "rgba(255,255,255,0.02)" }}>
                <th style={S.th} onClick={() => handleSort("registered_at")}>Date {renderSortIcon("registered_at")}</th>
                <th style={S.th} onClick={() => handleSort("full_name")}>Student {renderSortIcon("full_name")}</th>
                <th style={S.th} onClick={() => handleSort("plan_tier")}>Plan {renderSortIcon("plan_tier")}</th>
                <th style={S.th} onClick={() => handleSort("plan_status")}>Payment {renderSortIcon("plan_status")}</th>
                <th style={S.th} onClick={() => handleSort("lessons_remaining")}>Lessons {renderSortIcon("lessons_remaining")}</th>
                <th style={{ ...S.th, cursor: "pointer" }} onClick={() => handleSort("next_booking")}>Next Booking {renderSortIcon("next_booking")}</th>
                <th style={{ ...S.th, cursor: "default" }}>Referral</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={7} style={{ ...S.td, padding: 48, textAlign: "center", color: "rgba(255,255,255,0.3)" }}>No students found</td></tr>
              ) : (
                filtered.map((s) => {
                  const tierStyle = TIER_COLORS[s.plan_tier] || { bg: "rgba(255,255,255,0.05)", color: "rgba(255,255,255,0.3)" };
                  return (
                    <tr key={s.id} data-testid={`student-row-${s.id}`} style={{ transition: "background 0.15s" }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(255,255,255,0.03)"; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}>
                      {/* Date */}
                      <td style={S.td}>
                        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                          <span style={{ color: "#F5F5F7", fontSize: "0.82rem", whiteSpace: "nowrap" }}>{formatDate(s.registered_at)}</span>
                          {s.payment_date && (
                            <span style={{ color: "rgba(34,197,94,0.7)", fontSize: "0.72rem", whiteSpace: "nowrap" }}>Paid: {formatDate(s.payment_date)}</span>
                          )}
                        </div>
                      </td>
                      {/* Student */}
                      <td style={S.td}>
                        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                          <span style={{ color: "#F5F5F7", fontWeight: 600, fontSize: "0.86rem" }} data-testid={`student-name-${s.id}`}>
                            {s.full_name || "—"}
                          </span>
                          <span style={{ display: "flex", alignItems: "center", gap: 5, color: "rgba(255,255,255,0.45)", fontSize: "0.76rem", wordBreak: "break-all" }}>
                            <Mail style={{ width: 12, height: 12, flexShrink: 0 }} /> {s.email}
                          </span>
                          {s.phone && (
                            <span style={{ display: "flex", alignItems: "center", gap: 5, color: "rgba(255,255,255,0.35)", fontSize: "0.74rem" }}>
                              <Phone style={{ width: 12, height: 12, flexShrink: 0 }} /> {s.phone}
                            </span>
                          )}
                        </div>
                      </td>
                      {/* Plan */}
                      <td style={S.td}>
                        <span style={{ display: "inline-block", padding: "4px 10px", borderRadius: 6, fontSize: "0.74rem", fontWeight: 600, whiteSpace: "nowrap", background: tierStyle.bg, color: tierStyle.color }}>
                          {TIER_LABELS[s.plan_tier] || "No Plan"}
                        </span>
                      </td>
                      {/* Payment */}
                      <td style={S.td}>
                        <span style={{
                          display: "inline-block", padding: "4px 10px", borderRadius: 6, fontSize: "0.74rem", fontWeight: 600, whiteSpace: "nowrap",
                          background: s.plan_status === "paid" ? "rgba(34,197,94,0.15)" : "rgba(255,255,255,0.05)",
                          color: s.plan_status === "paid" ? "#22c55e" : "rgba(255,255,255,0.35)",
                        }}>
                          {s.plan_status === "paid" ? "Confirmed" : "Free"}
                        </span>
                      </td>
                      {/* Lessons */}
                      <td style={S.td}>
                        <span style={{ fontWeight: 700, fontSize: "1rem", color: s.lessons_remaining <= 2 ? "#ef4444" : "#F5F5F7" }}>
                          {s.lessons_remaining}
                        </span>
                      </td>
                      {/* Next Booking */}
                      <td style={S.td}>
                        {s.next_booking ? (
                          <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                            <span style={{ color: "#F5F5F7", fontSize: "0.82rem" }}>{s.next_booking.date}</span>
                            <span style={{ color: "rgba(34,197,94,0.8)", fontSize: "0.76rem" }}>{s.next_booking.time}</span>
                          </div>
                        ) : (
                          <span style={{ color: "rgba(255,255,255,0.2)" }}>—</span>
                        )}
                      </td>
                      {/* Referral */}
                      <td style={S.td}>
                        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                          <span style={{ fontFamily: "'SF Mono', 'Fira Code', monospace", fontSize: "0.78rem", color: "rgba(255,255,255,0.5)" }}>{s.referral_code || "—"}</span>
                          {(s.referral_balance > 0 || s.payout_requested) && (
                            <span style={{ fontWeight: 600, color: s.payout_requested ? "#eab308" : "#22c55e", fontSize: "0.82rem" }}>
                              {s.referral_balance.toFixed(2)} €
                              {s.payout_requested && <span style={{ display: "block", fontSize: "0.7rem", fontWeight: 500, color: "#eab308" }}>Payout Requested</span>}
                            </span>
                          )}
                          {s.payout_requested && (
                            <button
                              style={{
                                padding: "4px 10px", background: "rgba(34,197,94,0.15)", color: "#22c55e",
                                border: "1px solid rgba(34,197,94,0.3)", borderRadius: 6, fontSize: "0.72rem",
                                fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap",
                              }}
                              onClick={async () => {
                                try {
                                  const res = await fetch(`${API_URL}/api/admin/referral/mark-paid/${s.id}`, {
                                    method: "POST", headers,
                                  });
                                  if (res.ok) fetchStudents();
                                } catch (e) { /* silent */ }
                              }}
                              data-testid={`mark-paid-${s.id}`}
                            >
                              Mark as Paid
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        )}
      </div>
      <div style={S.footer} data-testid="admin-footer">
        Showing {filtered.length} of {total} students
      </div>
    </div>
  );
}
